/**
 * The sustainability areas a candidate claims, and at what depth.
 *
 * Read by the admin (every workspace that shows a candidate, through
 * `views/_talent_taxonomy.ts`) and by the employer dashboard on the public
 * site, through the `@config` alias. So it imports nothing: the site bundles
 * this file, and `tests/shared-config-imports.test.mjs` fails if it ever
 * reaches for a package or a panel module.
 *
 * The keys and labels mirror `collections/talents.ts`, duplicated rather than
 * imported because that file nests them four levels deep inside a property
 * definition — and because importing a collection is exactly what the site
 * must not do.
 */

/** Short names, sized for a chip in a dense list. */
export const AREA_LABELS: Record<string, string> = {
    estrategia_esg: "Estrategia ESG",
    informes_sostenibilidad: "Informes",
    gestion_gobernanza: "Gobernanza",
    compliance: "Compliance",
    materialidad: "Materialidad",
    cambio_climatico: "Cambio climático",
    energia: "Energía",
    agua: "Agua",
    economia_circular: "Economía circular",
    biodiversidad: "Biodiversidad",
    impacto_social: "Impacto social",
    dei: "DEI",
    derechos_humanos: "Derechos humanos",
    finanzas_sostenibles: "Finanzas sostenibles",
    inversion_impacto: "Inversión de impacto",
    cadena_suministro_sostenible: "Cadena de suministro",
    otro: "Otro"
};

/**
 * An earlier version of the questionnaire wrote English area keys, and those
 * rows are still live — 27 candidates carry `energy`, 26 `circular_economy`,
 * and so on. Unmapped they rendered as raw identifiers on the card, and because
 * a candidate can hold both spellings they were also counted twice, which is
 * where "18 áreas fuertes" came from when only 17 areas exist.
 *
 * Mapping them onto the canonical key fixes the label and the count together.
 */
const LEGACY_AREA_KEYS: Record<string, string> = {
    energy: "energia",
    social: "impacto_social",
    governance: "gestion_gobernanza",
    circular_economy: "economia_circular",
    climate: "cambio_climatico",
    esg_reporting: "informes_sostenibilidad",
    biodiversity: "biodiversidad",
    water: "agua"
};

/** Canonical key for an area, collapsing the legacy English vocabulary. */
export function canonicalAreaKey(key: string): string {
    return LEGACY_AREA_KEYS[key] ?? key;
}

export type AreaLevel = "none" | "interested" | "beginner" | "intermediate" | "advanced";

/**
 * Ranked so a card can sort areas by strength and cut the tail. `none` is 0 and
 * not merely absent: a candidate who explicitly answered "sin experiencia" is
 * different from one who skipped the question, and only the ranking collapses
 * them.
 */
export const LEVEL_RANK: Record<AreaLevel, number> = {
    none: 0,
    interested: 1,
    beginner: 2,
    intermediate: 3,
    advanced: 4
};

export const LEVEL_LABELS: Record<AreaLevel, string> = {
    none: "Sin experiencia",
    interested: "Interesado/a",
    beginner: "Principiante",
    intermediate: "Intermedio",
    advanced: "Avanzado"
};

export function isAreaLevel(value: unknown): value is AreaLevel {
    return typeof value === "string" && value in LEVEL_RANK;
}

/** An area the candidate claims, ready to render. */
export interface TalentArea {
    key: string;
    label: string;
    level: AreaLevel;
    levelLabel: string;
    rank: number;
}

/**
 * The areas a questionnaire claims, strongest first — and whether the question
 * was ever answered.
 *
 * Defensive throughout: several form versions wrote this field.
 * `sustainability_areas` is a map today but arrives as an array on older rows,
 * and both appear in production. An explicit "sin experiencia" is dropped from
 * the list (there is nothing to show for a no) but still counts as an answer.
 *
 * `answered` is the presence of the key, the same test `requirements.ts` uses
 * for credentials: an area a vacancy asks for and a candidate does not claim is
 * "no la declara" when they answered and "sin datos" when they never did.
 */
export function readAreas(questionnaire: unknown): { areas: TalentArea[]; answered: boolean } {
    const q = questionnaire && typeof questionnaire === "object" && !Array.isArray(questionnaire)
        ? questionnaire as Record<string, unknown>
        : {};
    const raw = q.sustainability_areas;

    // Keyed by canonical key so the two vocabularies collapse into one entry.
    // When a candidate holds both spellings the stronger level wins: the two are
    // the same claim recorded twice, not two separate ones.
    const byKey = new Map<string, TalentArea>();
    const add = (rawKey: string, level: AreaLevel) => {
        const key = canonicalAreaKey(rawKey);
        const existing = byKey.get(key);
        if (existing && existing.rank >= LEVEL_RANK[level]) return;
        byKey.set(key, {
            key,
            label: AREA_LABELS[key] ?? key,
            level,
            levelLabel: LEVEL_LABELS[level],
            rank: LEVEL_RANK[level]
        });
    };

    let answered = false;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        answered = true;
        for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
            const level = String(value ?? "none");
            if (!isAreaLevel(level)) continue;
            if (level === "none") continue; // nothing to show for an explicit no
            add(key, level);
        }
    } else if (Array.isArray(raw)) {
        answered = true;
        // Legacy shape: a bare list of area keys with no level attached. Treat
        // them as claimed-but-ungraded rather than dropping the candidate's
        // only signal.
        for (const key of raw) {
            if (typeof key !== "string") continue;
            add(key, "interested");
        }
    }

    const areas = [...byKey.values()];
    areas.sort((a, b) => b.rank - a.rank || a.label.localeCompare(b.label));
    return { areas, answered };
}

/**
 * Claiming "avanzado" across most of a 17-area vocabulary is the classic
 * inflated-profile shape — real practitioners specialize. Same threshold and
 * same reasoning as `assessCredibility` in the backend
 * (`services/match-credibility.ts`): half the board, because broad senior
 * profiles legitimately span five or six areas.
 */
export const INFLATED_ADVANCED_THRESHOLD = Math.ceil(Object.keys(AREA_LABELS).length / 2);

/** Advanced across half the vocabulary or more. Ranking must not reward this. */
export function isInflated(areas: TalentArea[]): boolean {
    return areas.filter(a => a.rank === LEVEL_RANK.advanced).length >= INFLATED_ADVANCED_THRESHOLD;
}
